import { useState, useMemo, useEffect, useRef } from "react";
import {
  FiRefreshCw,
  FiClock,
  FiCalendar,
  FiAlertCircle,
} from "react-icons/fi";
import { LuIndianRupee } from "react-icons/lu";

import Swal from "sweetalert2";

import DynamicTable from "../Common/DynamicTable";
import { renewalColumns } from "./RenewalsColumn";
import RenewalDrawer from "./RenewalDrawer";
import api from "../../api/axios";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";

import "../../assets/css/Renewal/RenewalsList.css";
import "../../assets/css/Common/stats_cards.css";

const DEFAULT_CARDS = {
  total_renewals: 0,
  expiry_in_90_days: 0,
  expiry_in_60_days: 0,
  expiry_in_30_days: 0,
  revenue: 0,
};

export default function RenewalsList() {
  const [renewals, setRenewals] = useState([]);
  const [cards, setCards] = useState(DEFAULT_CARDS);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedRenewal, setSelectedRenewal] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (filter = dateFilter) => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = {};
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const params = buildDateFilterParams(filter);
      if (Object.keys(params).length > 0) config.params = params;

      const [renewalsRes, cardsRes] = await Promise.all([
        Object.keys(config).length > 0 ? api.get("/renewal/", config) : api.get("/renewal/"),
        Object.keys(config).length > 0 ? api.get("/renewal/cards", config) : api.get("/renewal/cards"),
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      setRenewals(renewalsRes.data || []);
      setCards(cardsRes.data || DEFAULT_CARDS);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Error loading renewals:", err);
      setRenewals([]);
      setCards(DEFAULT_CARDS);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const loadRenewals = (filter) => fetchData(filter);
  const loadCards = (filter) => fetchData(filter);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter);
  };

  const openRenewal = async (id) => {
    try {
      const res = await api.get(`/renewal/${id}`);

      setSelectedRenewal(res.data);
      setDrawerOpen(true);
    } catch (err) {
      console.error("Error loading renewal:", err);
    }
  };

  const handleSelect = (id) => {
    const normalizedId = String(id);

    setSelectedIds((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((x) => x !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) {
      Swal.fire({
        icon: "warning",
        title: "No renewals selected",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Delete selected renewals?",
      text: `You are about to delete ${selectedIds.length} renewal(s).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete("/renewal/bulk-delete", {
        data: {
          renewal_ids: selectedIds,
        },
      });

      setRenewals((prev) =>
        prev.filter((r) => !selectedIds.includes(String(r.id)))
      );

      setSelectedIds([]);

      Swal.fire({
        icon: "success",
        title: "Deleted successfully",
      });

      await loadRenewals();
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Delete failed",
      });
    }
  };

  const filteredData = useMemo(() => {
    const safeSearch = (search || "").toLowerCase().trim();

    return renewals.filter((r) => {
      // Exclude renewals that already have an auto-generated lead
      if (r.auto_lead_id || r.auto_lead) {
        return false;
      }

      const orgName =
        typeof r.organization === "string"
          ? r.organization
          : (r.organization?.name || r.organization_name || "");
      const dealTitle =
        typeof r.deal === "string"
          ? r.deal
          : (r.deal?.title || r.deal?.description || "");
      const assignedTo =
        typeof r.assigned_to === "string"
          ? r.assigned_to
          : (r.assigned_to?.name || "");
      const remarksText =
        r.remarks ||
        r.notes ||
        r.remark ||
        r.organization_remarks ||
        r.renewal_remarks ||
        r.description ||
        "";
      const statusText = r.status || "";
      const amountText = r.renewal_amount || r.amount || "";
      const idText = r.id != null ? String(r.id) : "";

      const searchText = `
  ${orgName}
  ${dealTitle}
  ${assignedTo}
  ${remarksText}
  ${statusText}
  ${amountText}
  ${idText}
`.toLowerCase();

      const matchesSearch = !safeSearch || searchText.includes(safeSearch);

      const matchesStatus = statusFilter
        ? r.status === statusFilter
        : true;

      return matchesSearch && matchesStatus;
    });
  }, [renewals, search, statusFilter]);

  const statuses = useMemo(() => {
    return [...new Set(renewals.map((r) => r.status).filter(Boolean))];
  }, [renewals]);

  // Sorting logic
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const getSortValue = (row, key) => {
    switch (key) {
      case "title":
        return row.deal?.description || row.lead?.description || "";

      case "organization":
        return typeof row.organization === "string"
          ? row.organization
          : (row.organization?.name || row.organization_name || "");

      case "next_renewal_date":
        return row.next_renewal_date
          ? new Date(row.next_renewal_date).getTime()
          : 0;

      case "remaining_days":
        return Number(row.expiry_in_days ?? row.remaining_days ?? 0);

      case "renewal_amount":
      case "amount":
        return Number(row.amount ?? row.renewal_amount ?? 0);

      case "notes":
      case "remarks":
        return row.remarks || row.notes || row.remark || row.description || "";

      case "assigned_to":
        return typeof row.assigned_to === "string"
          ? row.assigned_to
          : (row.assigned_to?.name || "");

      default:
        return row[key];
    }
  };

  const sortedRenewals = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortConfig.direction === "asc"
          ? aVal - bVal
          : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredData, sortConfig]);

  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleSelectAll = () => {
    const allIds = sortedRenewals.map((r) => String(r.id));

    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  const canDelete =
    user?.role !== "Sales Executive" &&
    user?.role !== "Sales Manager";

  return (
    <div className="main-wrapper">
      <div className="page-header">
        <h2>Renewals</h2>
        <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
      </div>

      <div className={`stats-grid stats-grid-5${loading ? " refreshing" : ""}`}>
        <div className="stat-card">
          <div className="stat-icon blue">
            <FiRefreshCw />
          </div>
          <div>
            <p>Total Renewals</p>
            <h2>{cards?.total_renewals ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <FiCalendar />
          </div>
          <div>
            <p>Expiring in 90 Days</p>
            <h2>{cards?.expiry_in_90_days ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <FiClock />
          </div>
          <div>
            <p>Expiring in 60 Days</p>
            <h2>{cards?.expiry_in_60_days ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">
            <FiAlertCircle />
          </div>
          <div>
            <p>Expiring in 30 Days</p>
            <h2>{cards?.expiry_in_30_days ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <LuIndianRupee />
          </div>
          <div>
            <p>Revenue</p>
            <h2>{Number(cards?.revenue || 0).toLocaleString("en-IN")}</h2>
          </div>
        </div>
      </div>

      <div className="table-card">
        <DynamicTable
          data={sortedRenewals}
          columns={renewalColumns({
            selectedIds,
            handleSelect,
            handleSelectAll,
            totalRows: sortedRenewals.length,
          })}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search renewals..."
          onRowClick={openRenewal}
          selectedIds={selectedIds}
          selectedCount={selectedIds.length}
          addButtonText="Add Renewal"
          onBulkDelete={handleBulkDelete}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
          canDelete={canDelete}
          storageKey="renewals_columns"
          filters={[
            {
              name: "status",
              value: statusFilter,
              onChange: setStatusFilter,
              options: statuses,
              placeholder: "All Status",
            },
          ]}
        />
      </div>

      <RenewalDrawer
        renewal={selectedRenewal}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        refreshRenewals={loadRenewals}
      />
    </div>
  );
}